import { FC, useEffect } from 'react';
import { css } from '@emotion/react';
import { CountryCode } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { theme } from '@frontend/theme';
import { DropdownChip } from '@frontend/design-system';
import { ALLOWED_COUNTRY_CODES, COUNTRIES } from '../../constants';
import { countryCodeToPostalCodeLocale, postalCodeLocaleToCountryCode } from '../../helpers';
import { useGetCountryCode } from '../../hooks/api-hooks';
import { useConfigShallowStore } from '../../stores';

const COUNTRY_OPTIONS = COUNTRIES.map((country) => ({ id: country.value, label: country.label }));

// Overrides the DS active/inactive chip colors so the selector always reads as a light blue pill
const chipStyles = css`
  background-color: ${theme.colors.primary10} !important;
  border: 1px solid ${theme.colors.primary50} !important;
  margin-left: auto;
  height: 30px;
  flex-shrink: 0;

  * {
    color: ${theme.colors.primary50} !important;
    fill: ${theme.colors.primary50} !important;
  }
`;

// DropdownChip has no disabled prop; block interaction while the initial country lookup is pending
// so a quick user selection can't be overwritten once `initialCountryCode` resolves.
const disabledChipStyles = css`
  pointer-events: none;
  opacity: 0.5;
`;

interface Props {
  locationId: string;
}

export const CountrySelector: FC<Props> = ({ locationId }) => {
  const { countryCode: initialCountryCode, isLoading } = useGetCountryCode(locationId);
  const { postalCodeLocale, isPostalCodeLocaleRestored, updatePostalCodeLocale } = useConfigShallowStore(
    'postalCodeLocale',
    'isPostalCodeLocaleRestored',
    'updatePostalCodeLocale'
  );

  useEffect(() => {
    if (isPostalCodeLocaleRestored) return;
    if (initialCountryCode && ALLOWED_COUNTRY_CODES.includes(initialCountryCode)) {
      updatePostalCodeLocale(countryCodeToPostalCodeLocale(initialCountryCode));
    }
  }, [initialCountryCode, isPostalCodeLocaleRestored, updatePostalCodeLocale]);

  const selectedCountryCode = postalCodeLocaleToCountryCode(postalCodeLocale);

  return (
    <DropdownChip
      css={isLoading ? [chipStyles, disabledChipStyles] : chipStyles}
      selectedOption={COUNTRY_OPTIONS.find((option) => option.id === selectedCountryCode)}
      options={COUNTRY_OPTIONS}
      onSelectOption={(option) => updatePostalCodeLocale(countryCodeToPostalCodeLocale(option.id as CountryCode))}
      iconName='location-small'
      useActiveState
    />
  );
};
